/**
 * ReturnLocks — protection côté frontend contre les doubles soumissions
 * simultanées d'un même retour.
 *
 * Rôle :
 * - Empêcher deux clics quasi-simultanés d'aboutir à deux appels réseau.
 * - Servir de garde-fou local pendant que le backend (MongoDB ReturnLocks
 *   collection + TTL index) est la source de vérité unique.
 *
 * Architecture interne :
 * - Un seul backing-store persistant : window.localStorage (préfixe __idem_lock__).
 *   Toutes les opérations écrivent et lisent directement dans ce store.
 * - Un Map mémoire (`lockStore`) fait office de cache pour les accès répétés
 *   dans la même session — il est invalidé systématiquement à chaque écriture
 *   ou suppression du backing-store.
 *
 * États : PENDING → COMPLETED (succès) | FAILED (échec, permet retry)
 * TTL   : 30 secondes (RETURN_LOCK_TTL_MS)
 *
 * Helpers additionnels :
 * - `purgeExpiredReturnLocks()`     — nettoie les PENDING expirés (mémoire + LS)
 * - `clearAllReturnLocks()`         — reset mémoire seulement (cross-tab/reload)
 * - `purgeAllReturnLocks()`         — wipe total (mémoire + LS), tests only
 *
 * @see cashier.ts — generateIdempotencyKey / getOrCreateIdempotencyKey / clearIdempotencyKey
 */

// ---------------------------------------------------------------------------
// Constantes
// ---------------------------------------------------------------------------

/** Durée de vie d'un verrou en millisecondes (30 secondes). */
export const RETURN_LOCK_TTL_MS = 30_000;

/** États possibles d'un verrou. */
export type ReturnLockStatus = 'PENDING' | 'COMPLETED' | 'FAILED';

/** Structure interne d'un verrou persisté dans localStorage. */
interface ReturnLockRecord {
  status: ReturnLockStatus;
  createdAt: number;
}

// ---------------------------------------------------------------------------
// Module-level lock store (cache mémoire synchronisé)
// ---------------------------------------------------------------------------

const lockStore = new Map<string, ReturnLockRecord>();

const STORAGE_PREFIX = '__idem_lock__';

// ---------------------------------------------------------------------------
// Helpers autour du backing-store localStorage
// ---------------------------------------------------------------------------

function storageRead(key: string): ReturnLockRecord | null {
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${key}`);
    if (!raw) return null;
    return JSON.parse(raw) as ReturnLockRecord;
  } catch {
    return null;
  }
}

function storageWrite(key: string, record: ReturnLockRecord): void {
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${key}`, JSON.stringify(record));
  } catch {
    // silencieux (mode privé, quota dépassé)
  }
}

function storageDelete(key: string): void {
  try {
    window.localStorage.removeItem(`${STORAGE_PREFIX}${key}`);
  } catch {
    // silencieux
  }
}

function isExpired(record: ReturnLockRecord): boolean {
  return Date.now() - record.createdAt > RETURN_LOCK_TTL_MS;
}

function invalidateCache(key: string): void {
  lockStore.delete(key);
}

/** Cherche d'abord en mémoire, puis dans localStorage. Met en cache si trouvé. */
function resolveRecord(key: string): ReturnLockRecord | null {
  const mem = lockStore.get(key);
  if (mem) return mem;

  const stored = storageRead(key);
  if (stored) lockStore.set(key, stored);
  return stored;
}

// ---------------------------------------------------------------------------
// API publique
// ---------------------------------------------------------------------------

/**
 * Tente d'acquérir un verrou pour la clé d'idempotency donnée.
 *
 * Retourne :
 * - 'ACQUIRED'        — verrou créé (première soumission)
 * - 'ALREADY_DONE'    — verrou en COMPLETED, retour déjà finalisé
 * - 'ALREADY_PENDING' — verrou PENDING encore valide, soumission en cours
 * - 'ERROR'           — clé vide/invalide ou stockage indisponible
 */
export type AcquireResult =
  | { result: 'ACQUIRED' }
  | { result: 'ALREADY_DONE' }
  | { result: 'ALREADY_PENDING' }
  | { result: 'ERROR'; reason: string };

export const acquireReturnLock = (idempotencyKey: string): AcquireResult => {
  // --- Garde : clé vide --------------------------------------------------
  if (!idempotencyKey || typeof idempotencyKey !== 'string' || !idempotencyKey.trim()) {
    return { result: 'ERROR', reason: 'idempotencyKey is empty or invalid' };
  }

  const key = idempotencyKey.trim();

  // --- 1. Backing-store localStorage (persiste cross-tab / rechargement) --
  const stored = storageRead(key);
  if (stored) {
    if (stored.status === 'COMPLETED') {
      lockStore.set(key, stored);
      return { result: 'ALREADY_DONE' };
    }
    if (stored.status === 'FAILED' || isExpired(stored)) {
      storageDelete(key);
      invalidateCache(key);
    } else {
      // PENDING valide
      lockStore.set(key, stored);
      return { result: 'ALREADY_PENDING' };
    }
  }

  // --- 2. Cache mémoire (même session navigateur) -------------------------
  const mem = lockStore.get(key);
  if (mem) {
    if (mem.status === 'COMPLETED') {
      return { result: 'ALREADY_DONE' };
    }
    if (mem.status === 'FAILED' || isExpired(mem)) {
      invalidateCache(key);
    } else {
      return { result: 'ALREADY_PENDING' };
    }
  }

  // --- 3. Créer le verrou PENDING -----------------------------------------
  const record: ReturnLockRecord = { status: 'PENDING', createdAt: Date.now() };
  storageWrite(key, record);
  lockStore.set(key, record);

  return { result: 'ACQUIRED' };
};

/**
 * Libère un verrou — no-op silencieux si clé absente ou invalide.
 * @param status 'COMPLETED' (défaut) ou 'FAILED'
 */
export const releaseReturnLock = (
  idempotencyKey: string,
  status: ReturnLockStatus = 'COMPLETED',
): void => {
  if (!idempotencyKey || typeof idempotencyKey !== 'string' || !idempotencyKey.trim()) return;

  const key = idempotencyKey.trim();
  const existing = lockStore.get(key);
  const record: ReturnLockRecord = {
    status,
    createdAt: existing?.createdAt ?? Date.now(),
  };

  storageWrite(key, record);
  lockStore.set(key, record);
};

/**
 * Nettoie les verrous PENDING expirés (TTL > 30 s) du cache mémoire
 * ET du backing-store. Les verrous COMPLETED et FAILED sont conservés.
 *
 * @return Nombre de verrous purgés.
 *
 * @see clearAllReturnLocks — pour un reset mémoire seulement (cross-tab)
 * @see purgeAllReturnLocks — pour un wipe total (tests)
 */
export const purgeExpiredReturnLocks = (): number => {
  let purged = 0;

  // Cache mémoire
  for (const [key, record] of lockStore.entries()) {
    if (isExpired(record)) {
      lockStore.delete(key);
      storageDelete(key);
      purged++;
    }
  }

  // Backing-store — collecte les clés avant suppression
  try {
    const keysToCheck: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const fullKey = window.localStorage.key(i);
      if (fullKey && fullKey.startsWith(STORAGE_PREFIX)) {
        keysToCheck.push(fullKey);
      }
    }
    for (const fullKey of keysToCheck) {
      const raw = window.localStorage.getItem(fullKey);
      if (raw) {
        try {
          const record: ReturnLockRecord = JSON.parse(raw);
          if (isExpired(record)) {
            window.localStorage.removeItem(fullKey);
            invalidateCache(fullKey.slice(STORAGE_PREFIX.length));
            purged++;
          }
        } catch {
          window.localStorage.removeItem(fullKey);
          purged++;
        }
      }
    }
  } catch {
    // silencieux
  }

  return purged;
};

/**
 * Retourne l'état vu par l'UI :
 * - 'LOCKED_PENDING'   — soumission en cours
 * - 'LOCKED_COMPLETED' — retour déjà finalisé (verrouillé pour protection)
 * - 'FREE'             — verrou libre
 */
export type LockViewStatus = 'LOCKED_PENDING' | 'LOCKED_COMPLETED' | 'FREE';

export const isLocked = (idempotencyKey: string): LockViewStatus => {
  if (!idempotencyKey || typeof idempotencyKey !== 'string' || !idempotencyKey.trim()) {
    return 'FREE';
  }

  const key = idempotencyKey.trim();

  // Cache mémoire d'abord
  const mem = lockStore.get(key);
  if (mem) {
    if (mem.status === 'COMPLETED') return 'LOCKED_COMPLETED';
    if (mem.status === 'PENDING' && !isExpired(mem)) return 'LOCKED_PENDING';
    // FAILED / expired → invalide le cache et continue vers le backing-store
    invalidateCache(key);
  }

  // Backing-store
  const stored = storageRead(key);
  if (stored) {
    if (stored.status === 'COMPLETED') return 'LOCKED_COMPLETED';
    if (stored.status === 'PENDING' && !isExpired(stored)) {
      lockStore.set(key, stored);
      return 'LOCKED_PENDING';
    }
    // FAILED / expired → cleanup silencieux
    storageDelete(key);
    invalidateCache(key);
  }

  return 'FREE';
};

/**
 * Réinitialise le verrouillage — vide uniquement le cache mémoire.
 *
 * Le backing-store localStorage est intentionnellement préservé,
 * car il représente l'état partagé entre onglets / rechargements.
 * Cette fonction simule un rechargement de navigateur ou l'ouverture
 * d'un nouvel onglet : le Map mémoire est frais, mais les verrous
 * persistés dans localStorage sont relus depuis là au besoin.
 *
 * Utilité : tests cross-tab / cross-reload, reset global.
 */
export const clearAllReturnLocks = (): void => {
  lockStore.clear();
};

/**
 * Wipe total : vide le cache mémoire ET supprime tous les verrous du
 * backing-store localStorage. Utilisé UNIQUEMENT dans les tests pour
 * garantir un état propre entre chaque cas de test indépendant.
 *
 * @see clearAllReturnLocks — pour un reset mémoire seulement (cross-tab)
 * @see purgeExpiredReturnLocks — pour un nettoyage sélectif en production
 */
export const purgeAllReturnLocks = (): void => {
  lockStore.clear();
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const fullKey = window.localStorage.key(i);
      if (fullKey && fullKey.startsWith(STORAGE_PREFIX)) {
        keysToRemove.push(fullKey);
      }
    }
    for (const k of keysToRemove) window.localStorage.removeItem(k);
  } catch {
    // silencieux
  }
};
